/**
 * Spell VFX data table (pure). Every player ability is mapped explicitly; enemy abilities and
 * anything unmapped resolve through enemy-kind and school fallbacks. Recipes in director.ts read
 * these specs; nothing here touches three.js.
 *
 * Readability rules baked into the palettes:
 *  - hostile (enemy-sourced) effects are warm and saturated but moderate;
 *  - friendly effects lean cool and run at lower opacity (`FRIENDLY_ALPHA`), so they never
 *    hide danger zones on the floor.
 * Colours are linear RGB in 0..1 and deliberately below the bloom threshold on their own; impact
 * comes from motion, shape and timing, not from brightness.
 */
import { BASE_ABILITY, BASE_AURA, BASE_VISUAL_ALIAS } from '@keystone/content'; // +classes
import type { AbilityId, EnemyAbilityKind, School, Targeting } from '@keystone/sim';
import { AUTO_SHOT_ABILITY_ID } from '@keystone/sim'; // +autoattack
import { ABILITY, AURA, BLOODREAVER, ENEMY, ENEMY_AURA, IRONCLAD, RAVAGER } from '@keystone/content';
import { CINDERBORN, CINDERBORN_AURA, RIMECALLER, RIMECALLER_AURA, STORMCALLER, STORMCALLER_AURA } from '@keystone/content'; // +mage-specs
import { ENEMY_VFX } from './enemyTable';
import { MIRAGE, MIRAGE_AURA, SHADOWDANCER, SHADOWDANCER_AURA, VENOMWRIGHT, VENOMWRIGHT_AURA } from '@keystone/content'; // +rogue-specs
import { HEMOMANCER, HEMOMANCER_AURA, HERETIC, HERETIC_AURA, LIGHTBINDER, LIGHTBINDER_AURA } from '@keystone/content'; // +acolyte-specs
import { BEASTBOUND, BEASTBOUND_AURA, DEADEYE, DEADEYE_AURA, GROVEWARDEN, GROVEWARDEN_AURA } from '@keystone/content'; // +ranger-specs

export type RGB = readonly [number, number, number];

export type CastStyle = 'none' | 'spark' | 'flame' | 'holy' | 'storm' | 'shadow' | 'venom' | 'windup';
export type ReleaseStyle = 'none' | 'projectile' | 'bolt' | 'chain' | 'swing' | 'nova' | 'wave' | 'flash' | 'beam' | 'charge';
export type ProjectileStyle = 'lava' | 'orb' | 'holy' | 'venom' | 'shadow' | 'wind' | 'fire' | 'frost' | 'arrow' | 'bullet'; // +classes: arrow = a real arrow mesh; +ranger-specs: bullet = a flat, near-instant tracer with a muzzle flash
export type ImpactStyle =
  | 'none'
  | 'sparks'
  | 'lightning'
  | 'fire'
  | 'flameLick'
  | 'holy'
  | 'holyBurst'
  | 'slash'
  | 'heavy'
  | 'shieldSlam'
  | 'execute'
  | 'smash'
  | 'taunt'
  | 'venom'
  | 'shadow'
  | 'arcane'
  | 'frost'
  | 'heal'
  | 'bigHeal'
  | 'motes'
  | 'cleanse';
export type TickStyle = 'none' | 'flameLick' | 'quake' | 'holy' | 'venom' | 'motes' | 'bleed' | 'blade' | 'school';
export type DecalStyle = 'none' | 'scorch' | 'frost' | 'glyph' | 'cracks';
export type SwingStyle = 'slash' | 'wide' | 'overhead' | 'thrust' | 'spin';
export type ZoneStyle = 'quake' | 'consecrate' | 'venomPool' | 'embers' | 'star' | 'snare'; // +basekits: star = Wandering Star's drifting orb, snare = an armed trap
export type AuraStyle =
  | 'stormkeeper'
  | 'bubble'
  | 'divine'
  | 'aegis'
  | 'astral'
  | 'shieldWall'
  | 'bladestorm'
  | 'parry'
  | 'burning'
  | 'smash'
  | 'hypnosis'
  | 'petrified'
  | 'ward'
  | 'freedom'
  | 'motes'
  | 'venom'
  | 'bleed'
  | 'ritual';

export interface VfxSpec {
  /** Table key or fallback name (debug). */
  key: string;
  color: RGB;
  /** Hot core / highlight colour. */
  accent: RGB;
  cast: CastStyle;
  release: ReleaseStyle;
  projectile?: ProjectileStyle;
  /** Projectile speed in yd/s. */
  speed?: number;
  swing?: SwingStyle;
  impact: ImpactStyle;
  /** Style for heal events of this ability. */
  heal: ImpactStyle;
  tick: TickStyle;
  decal: DecalStyle;
  zone?: ZoneStyle;
  /** Overall size multiplier. */
  scale: number;
  /** Crits may trigger hit-stop / screen shake. */
  big: boolean;
  hostile: boolean;
}

export interface AuraVfx {
  style: AuraStyle;
  color: RGB;
  accent: RGB;
}

/** Friendly effects render at this opacity relative to hostile ones. */
export const FRIENDLY_ALPHA = 0.78;

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

export const C = {
  arrow: [0.85, 0.7, 0.45] as RGB, // +classes: Ranger arrows
  arrowCore: [1.0, 0.95, 0.8] as RGB,
  storm: [0.42, 0.66, 1.0],
  stormCore: [0.82, 0.9, 1.0],
  lava: [1.0, 0.36, 0.08],
  lavaCore: [1.0, 0.72, 0.36],
  flame: [1.0, 0.46, 0.14],
  earth: [0.56, 0.46, 0.36],
  quakeGlow: [0.52, 0.48, 0.95],
  holy: [1.0, 0.82, 0.42],
  holyCore: [1.0, 0.94, 0.76],
  heal: [0.5, 0.95, 0.78],
  healCore: [0.86, 1.0, 0.9],
  steel: [0.72, 0.8, 0.95],
  steelCore: [0.94, 0.96, 1.0],
  rage: [1.0, 0.38, 0.22],
  rageCore: [1.0, 0.75, 0.55],
  shield: [0.4, 0.62, 1.0],
  shieldCore: [0.8, 0.9, 1.0],
  wind: [0.6, 0.92, 0.95],
  interrupt: [0.55, 0.88, 1.0],
  interruptCore: [0.92, 0.98, 1.0],
  shadow: [0.55, 0.32, 0.9],
  // Rotation-rework accents (friendly).
  amber: [0.95, 0.58, 0.18],
  amberCore: [1.0, 0.84, 0.5],
  arc: [0.55, 0.78, 1.0],
  tempest: [0.3, 0.52, 1.0],
  aegis: [1.0, 0.8, 0.36],
  riposte: [0.62, 0.72, 0.95],
  dawn: [1.0, 0.8, 0.5],
  sun: [1.0, 0.68, 0.3],
  sunCore: [1.0, 0.9, 0.66],
  blood: [0.85, 0.12, 0.12],
  // Hostile palette: warm, saturated, moderate.
  venom: [0.68, 0.88, 0.12],
  venomCore: [0.95, 0.95, 0.45],
  hostilePhys: [1.0, 0.42, 0.16],
  hostilePhysCore: [1.0, 0.72, 0.42],
  hostileShadow: [0.85, 0.28, 0.75],
  hostileArcane: [0.95, 0.38, 0.62],
  hostileFire: [1.0, 0.36, 0.08],
  hostileFrost: [0.45, 0.72, 1.0],
  hostileHoly: [1.0, 0.7, 0.3],
  deathSmoke: [0.1, 0.09, 0.13],
  soul: [0.5, 0.44, 0.88],
} as const satisfies Record<string, RGB>;

const FRIENDLY_SCHOOL: Record<School, [RGB, RGB]> = {
  physical: [C.steel, C.steelCore],
  holy: [C.holy, C.holyCore],
  fire: [C.flame, C.lavaCore],
  nature: [C.storm, C.stormCore],
  frost: [[0.5, 0.8, 1.0], [0.85, 0.95, 1.0]],
  shadow: [C.shadow, [0.8, 0.7, 1.0]],
  arcane: [[0.7, 0.5, 1.0], [0.92, 0.85, 1.0]],
};

const HOSTILE_SCHOOL: Record<School, [RGB, RGB]> = {
  physical: [C.hostilePhys, C.hostilePhysCore],
  holy: [C.hostileHoly, C.holyCore],
  fire: [C.hostileFire, C.lavaCore],
  nature: [C.venom, C.venomCore],
  frost: [C.hostileFrost, [0.85, 0.95, 1.0]],
  shadow: [C.hostileShadow, [1.0, 0.6, 0.9]],
  arcane: [C.hostileArcane, [1.0, 0.7, 0.85]],
};

export function schoolColors(school: School | null, hostile: boolean): [RGB, RGB] {
  return (hostile ? HOSTILE_SCHOOL : FRIENDLY_SCHOOL)[school ?? 'physical'];
}

const IMPACT_BY_SCHOOL: Record<School, ImpactStyle> = {
  physical: 'sparks',
  holy: 'holy',
  fire: 'fire',
  nature: 'venom',
  frost: 'frost',
  shadow: 'shadow',
  arcane: 'arcane',
};

const PROJECTILE_BY_SCHOOL: Record<School, ProjectileStyle> = {
  physical: 'orb',
  holy: 'holy',
  fire: 'fire',
  nature: 'venom',
  frost: 'frost',
  shadow: 'shadow',
  arcane: 'orb',
};

const CAST_BY_SCHOOL: Record<School, CastStyle> = {
  physical: 'windup',
  holy: 'holy',
  fire: 'flame',
  nature: 'venom',
  frost: 'spark',
  shadow: 'shadow',
  arcane: 'shadow',
};

// ---------------------------------------------------------------------------
// Player abilities (explicit)
// ---------------------------------------------------------------------------

type Partial2 = Partial<VfxSpec> & Pick<VfxSpec, 'color' | 'accent'>;

const base = (key: string, p: Partial2): VfxSpec => ({
  key,
  cast: 'none',
  release: 'none',
  impact: 'none',
  heal: 'heal',
  tick: 'none',
  decal: 'none',
  scale: 1,
  big: false,
  hostile: false,
  ...p,
});

const S = ABILITY.stormcaller;
const W = ABILITY.warden;
const L = ABILITY.lightbinder;
const B = ABILITY.blademaster;

const RA = BASE_ABILITY.ranger; // +classes
const WA = BASE_ABILITY.warrior; // +basekits
const RO = BASE_ABILITY.rogue;
const MA = BASE_ABILITY.mage;
const AC = BASE_ABILITY.acolyte;
const STAR: RGB = [0.72, 0.78, 1.0];
const STAR_CORE: RGB = [1.0, 0.97, 0.88];
const FROST: RGB = [0.55, 0.85, 1.0];
const FROST_CORE: RGB = [0.9, 0.97, 1.0];

// +specs: specialisation looks (docs/design/classes/specs.md), existing styles recoloured per spec.
const IRON: RGB = [0.62, 0.68, 0.78];
const IRON_CORE: RGB = [0.92, 0.95, 1.0];
const HEAT: RGB = [1.0, 0.36, 0.2];
const HEAT_CORE: RGB = [1.0, 0.72, 0.5];
const GORE: RGB = [0.72, 0.06, 0.12];
const GORE_CORE: RGB = [1.0, 0.36, 0.4];
// +mage-specs: blue-white overvolt lightning, pale glacier frost, ember orange with a sun-gold core.
const VOLT: RGB = [0.36, 0.55, 1.0];
const VOLT_CORE: RGB = [0.86, 0.93, 1.0];
const RIME: RGB = [0.6, 0.88, 1.0];
const RIME_CORE: RGB = [0.94, 0.99, 1.0];
const EMBER: RGB = [1.0, 0.42, 0.12];
const EMBER_CORE: RGB = [1.0, 0.8, 0.45];

// +rogue-specs palettes.
const SHADE: RGB = [0.42, 0.3, 0.85];
const SHADE_CORE: RGB = [0.8, 0.72, 1.0];
const MIRROR: RGB = [0.5, 0.85, 0.95];
const MIRROR_CORE: RGB = [0.88, 0.98, 1.0];
const TOXIN: RGB = [0.45, 0.85, 0.2];
const TOXIN_CORE: RGB = [0.85, 1.0, 0.55];
// +acolyte-specs palettes: warm ward-gold (Lightbinder), arterial red (Hemomancer), hollow violet (Heretic).
const WARD: RGB = [1.0, 0.84, 0.42];
const WARD_CORE: RGB = [1.0, 0.97, 0.82];
const VEIN: RGB = [0.72, 0.05, 0.14];
const VEIN_CORE: RGB = [1.0, 0.42, 0.48];
const HOLLOW: RGB = [0.46, 0.24, 0.72];
const HOLLOW_CORE: RGB = [0.8, 0.56, 1.0];
// +ranger-specs palettes: brass and gunsmoke (Deadeye), leaf and blossom (Grovewarden), spirit amber (Beastbound).
const BRASS: RGB = [1.0, 0.72, 0.32];
const BRASS_CORE: RGB = [1.0, 0.95, 0.75];
const LEAF: RGB = [0.42, 0.88, 0.38];
const LEAF_CORE: RGB = [0.88, 1.0, 0.78];
const BLOSSOM: RGB = [1.0, 0.62, 0.8];
const BLOSSOM_CORE: RGB = [1.0, 0.92, 0.96];
const SPIRIT: RGB = [1.0, 0.62, 0.22];
const SPIRIT_CORE: RGB = [1.0, 0.9, 0.62];

/** Spec abilities with their own look (class agents add theirs; the rest borrow ui.animAs). */
const SPEC_VFX: Readonly<Record<AbilityId, VfxSpec>> = {
  // Ironclad: iron plates, sparks and shrapnel.
  [IRONCLAD.rivetStrike]: base(IRONCLAD.rivetStrike, { color: IRON, accent: IRON_CORE, release: 'swing', swing: 'overhead', impact: 'shieldSlam', scale: 1.0 }),
  [IRONCLAD.anchorChain]: base(IRONCLAD.anchorChain, { color: IRON, accent: IRON_CORE, release: 'beam', impact: 'heavy', scale: 0.9 }),
  [IRONCLAD.shieldSweep]: base(IRONCLAD.shieldSweep, { color: IRON, accent: IRON_CORE, release: 'swing', swing: 'wide', impact: 'slash', scale: 0.95 }),
  [IRONCLAD.shrapnelBurst]: base(IRONCLAD.shrapnelBurst, { color: IRON, accent: IRON_CORE, release: 'nova', impact: 'sparks', tick: 'bleed', decal: 'cracks', scale: 1.25, big: true }),
  [IRONCLAD.wreckage]: base(IRONCLAD.wreckage, { color: IRON, accent: IRON_CORE, release: 'swing', swing: 'overhead', impact: 'heavy', heal: 'heal', scale: 1.05, big: true }),
  [IRONCLAD.platedBash]: base(IRONCLAD.platedBash, { color: IRON, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.85 }),
  [IRONCLAD.plateAegis]: base(IRONCLAD.plateAegis, { color: IRON, accent: IRON_CORE, release: 'flash', scale: 0.9 }),
  [IRONCLAD.ironMaiden]: base(IRONCLAD.ironMaiden, { color: IRON, accent: IRON_CORE, release: 'nova', impact: 'sparks', scale: 1.1 }),
  // Ravager: red-hot steel, both blades.
  [RAVAGER.twinReaping]: base(RAVAGER.twinReaping, { color: HEAT, accent: HEAT_CORE, release: 'swing', swing: 'spin', impact: 'slash', scale: 1.15, big: true }),
  [RAVAGER.breakneck]: base(RAVAGER.breakneck, { color: HEAT, accent: HEAT_CORE, release: 'charge', impact: 'heavy', scale: 0.9 }),
  [RAVAGER.steelCyclone]: base(RAVAGER.steelCyclone, { color: C.steel, accent: HEAT_CORE, release: 'swing', swing: 'spin', impact: 'slash', scale: 1.0 }),
  [RAVAGER.redHunger]: base(RAVAGER.redHunger, { color: HEAT, accent: HEAT_CORE, release: 'swing', swing: 'slash', impact: 'slash', heal: 'heal', scale: 0.95 }),
  [RAVAGER.headsman]: base(RAVAGER.headsman, { color: HEAT, accent: HEAT_CORE, release: 'swing', swing: 'overhead', impact: 'execute', scale: 1.25, big: true }),
  [RAVAGER.hiltCrack]: base(RAVAGER.hiltCrack, { color: C.steel, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.8 }),
  [RAVAGER.wrathblood]: base(RAVAGER.wrathblood, { color: HEAT, accent: HEAT_CORE, release: 'flash', heal: 'bigHeal', scale: 0.9 }),
  [RAVAGER.unchained]: base(RAVAGER.unchained, { color: HEAT, accent: HEAT_CORE, release: 'nova', scale: 1.2, big: true }),
  // Bloodreaver: crimson, wounds and drinking.
  [BLOODREAVER.bloodwrench]: base(BLOODREAVER.bloodwrench, { color: GORE, accent: GORE_CORE, release: 'swing', swing: 'overhead', impact: 'execute', heal: 'bigHeal', scale: 1.2, big: true }),
  [BLOODREAVER.gorecharge]: base(BLOODREAVER.gorecharge, { color: GORE, accent: GORE_CORE, release: 'charge', impact: 'slash', tick: 'bleed', scale: 0.9 }),
  [BLOODREAVER.rendingArc]: base(BLOODREAVER.rendingArc, { color: GORE, accent: GORE_CORE, release: 'swing', swing: 'wide', impact: 'slash', tick: 'bleed', scale: 1.0 }),
  [BLOODREAVER.bleedingEdge]: base(BLOODREAVER.bleedingEdge, { color: GORE, accent: GORE_CORE, release: 'swing', swing: 'slash', impact: 'slash', tick: 'bleed', scale: 0.95 }),
  [BLOODREAVER.finalDraught]: base(BLOODREAVER.finalDraught, { color: GORE, accent: GORE_CORE, release: 'swing', swing: 'overhead', impact: 'execute', heal: 'heal', scale: 1.2, big: true }),
  [BLOODREAVER.crimsonKick]: base(BLOODREAVER.crimsonKick, { color: GORE, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', heal: 'heal', scale: 0.8 }),
  [BLOODREAVER.sanguineVigor]: base(BLOODREAVER.sanguineVigor, { color: GORE, accent: GORE_CORE, release: 'flash', heal: 'bigHeal', scale: 0.95 }),
  [BLOODREAVER.bloodletting]: base(BLOODREAVER.bloodletting, { color: GORE, accent: GORE_CORE, release: 'flash', impact: 'slash', scale: 0.7 }),
  [BLOODREAVER.crimsonTrance]: base(BLOODREAVER.crimsonTrance, { color: GORE, accent: GORE_CORE, release: 'nova', scale: 1.2, big: true }),
  // +mage-specs: Stormcaller: overvolted lightning; Skybreak is the biggest chain in the game.
  [STORMCALLER.spark]: base(STORMCALLER.spark, { color: VOLT, accent: VOLT_CORE, cast: 'storm', release: 'bolt', impact: 'lightning', scale: 0.9 }),
  [STORMCALLER.skybreak]: base(STORMCALLER.skybreak, { color: VOLT, accent: VOLT_CORE, cast: 'storm', release: 'chain', impact: 'lightning', scale: 1.4, big: true }),
  [STORMCALLER.staticCage]: base(STORMCALLER.staticCage, { color: VOLT, accent: VOLT_CORE, release: 'nova', impact: 'lightning', scale: 1.25 }),
  [STORMCALLER.staticStep]: base(STORMCALLER.staticStep, { color: VOLT, accent: VOLT_CORE, release: 'flash', impact: 'lightning', scale: 0.7 }),
  [STORMCALLER.chainStorm]: base(STORMCALLER.chainStorm, { color: VOLT, accent: VOLT_CORE, release: 'chain', impact: 'lightning', decal: 'scorch', scale: 1.1 }),
  [STORMCALLER.arcWard]: base(STORMCALLER.arcWard, { color: VOLT, accent: VOLT_CORE, release: 'flash', scale: 0.9 }),
  [STORMCALLER.wanderingTempest]: base(STORMCALLER.wanderingTempest, { color: VOLT, accent: VOLT_CORE, release: 'flash', zone: 'star', impact: 'lightning', scale: 1.35, big: true }),
  [STORMCALLER.stormheart]: base(STORMCALLER.stormheart, { color: VOLT, accent: VOLT_CORE, release: 'nova', scale: 1.2, big: true }),
  // Rimecaller: frost bolts, a glacial lance, freezing rings and a snowstorm.
  [RIMECALLER.rimeTithe]: base(RIMECALLER.rimeTithe, { color: RIME, accent: RIME_CORE, cast: 'spark', release: 'projectile', projectile: 'frost', speed: 36, impact: 'frost', scale: 0.95 }),
  [RIMECALLER.hoarlance]: base(RIMECALLER.hoarlance, { color: RIME, accent: RIME_CORE, release: 'projectile', projectile: 'frost', speed: 60, impact: 'frost', decal: 'frost', scale: 1.35, big: true }),
  [RIMECALLER.rimeRing]: base(RIMECALLER.rimeRing, { color: RIME, accent: RIME_CORE, release: 'nova', impact: 'frost', decal: 'frost', scale: 1.3 }),
  [RIMECALLER.frostfold]: base(RIMECALLER.frostfold, { color: RIME, accent: RIME_CORE, release: 'nova', impact: 'frost', decal: 'frost', scale: 0.9 }),
  [RIMECALLER.whiteout]: base(RIMECALLER.whiteout, { color: RIME, accent: RIME_CORE, release: 'flash', zone: 'embers', impact: 'frost', tick: 'school', decal: 'frost', scale: 1.1 }),
  [RIMECALLER.frostMantle]: base(RIMECALLER.frostMantle, { color: RIME, accent: RIME_CORE, release: 'flash', scale: 0.9 }),
  [RIMECALLER.wanderingGlacier]: base(RIMECALLER.wanderingGlacier, { color: RIME, accent: RIME_CORE, release: 'flash', zone: 'star', impact: 'frost', decal: 'frost', scale: 1.35, big: true }),
  [RIMECALLER.deepWinter]: base(RIMECALLER.deepWinter, { color: RIME, accent: RIME_CORE, release: 'nova', impact: 'frost', decal: 'frost', scale: 1.4, big: true }),
  // Cinderborn: ember lashes, a meteor of its own fire, a dive of flame, a small sun.
  [CINDERBORN.emberLash]: base(CINDERBORN.emberLash, { color: EMBER, accent: EMBER_CORE, cast: 'flame', release: 'projectile', projectile: 'fire', speed: 40, impact: 'flameLick', tick: 'flameLick', zone: 'embers', scale: 0.95 }), // zone: Kindled Heart's burning ground
  [CINDERBORN.cinderfall]: base(CINDERBORN.cinderfall, { color: EMBER, accent: EMBER_CORE, cast: 'flame', release: 'projectile', projectile: 'lava', speed: 30, impact: 'fire', decal: 'scorch', zone: 'embers', scale: 1.4, big: true }),
  [CINDERBORN.infernoRing]: base(CINDERBORN.infernoRing, { color: EMBER, accent: EMBER_CORE, release: 'nova', impact: 'fire', decal: 'scorch', scale: 1.3 }),
  [CINDERBORN.phoenixDive]: base(CINDERBORN.phoenixDive, { color: EMBER, accent: EMBER_CORE, release: 'charge', impact: 'fire', heal: 'heal', scale: 1.0 }),
  [CINDERBORN.pyreBurst]: base(CINDERBORN.pyreBurst, { color: EMBER, accent: EMBER_CORE, release: 'flash', zone: 'embers', impact: 'fire', tick: 'flameLick', decal: 'scorch', scale: 1.15 }),
  [CINDERBORN.ashrisePact]: base(CINDERBORN.ashrisePact, { color: EMBER, accent: EMBER_CORE, release: 'flash', scale: 0.9 }),
  [CINDERBORN.wanderingSun]: base(CINDERBORN.wanderingSun, { color: C.sun, accent: C.sunCore, release: 'flash', zone: 'star', impact: 'fire', heal: 'motes', scale: 1.35, big: true }),
  [CINDERBORN.kindledHeart]: base(CINDERBORN.kindledHeart, { color: EMBER, accent: EMBER_CORE, release: 'nova', scale: 1.2, big: true }),
  // +rogue-specs: Shadowdancer (violet shadow), Mirage (pale mirror-glass), Venomwright (green toxins).
  [SHADOWDANCER.twinFang]: base(SHADOWDANCER.twinFang, { color: SHADE, accent: SHADE_CORE, release: 'swing', swing: 'slash', impact: 'slash', scale: 0.85 }),
  [SHADOWDANCER.umbralFinale]: base(SHADOWDANCER.umbralFinale, { color: SHADE, accent: SHADE_CORE, release: 'swing', swing: 'overhead', impact: 'execute', scale: 1.15, big: true }),
  [SHADOWDANCER.duskwalk]: base(SHADOWDANCER.duskwalk, { color: SHADE, accent: SHADE_CORE, release: 'flash', scale: 0.8 }),
  [SHADOWDANCER.gloamstrike]: base(SHADOWDANCER.gloamstrike, { color: SHADE, accent: SHADE_CORE, release: 'swing', swing: 'thrust', impact: 'shadow', scale: 1.0, big: true }),
  [SHADOWDANCER.umbralStep]: base(SHADOWDANCER.umbralStep, { color: SHADE, accent: SHADE_CORE, release: 'flash', impact: 'shadow', scale: 0.8 }),
  [SHADOWDANCER.shadowFan]: base(SHADOWDANCER.shadowFan, { color: SHADE, accent: SHADE_CORE, release: 'swing', swing: 'spin', impact: 'slash', scale: 0.95 }),
  [SHADOWDANCER.veilOfNight]: base(SHADOWDANCER.veilOfNight, { color: SHADE, accent: SHADE_CORE, release: 'nova', scale: 0.9 }),
  [SHADOWDANCER.shadeJab]: base(SHADOWDANCER.shadeJab, { color: SHADE, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.8 }),
  [SHADOWDANCER.umbralConvergence]: base(SHADOWDANCER.umbralConvergence, { color: SHADE, accent: SHADE_CORE, release: 'nova', scale: 1.2, big: true }),
  [SHADOWDANCER.silhouetteEcho]: base(SHADOWDANCER.silhouetteEcho, { color: SHADE, accent: SHADE_CORE, impact: 'shadow', scale: 0.9, big: true }),
  [SHADOWDANCER.silhouetteFan]: base(SHADOWDANCER.silhouetteFan, { color: SHADE, accent: SHADE_CORE, impact: 'slash', scale: 0.6 }),
  [MIRAGE.falsestep]: base(MIRAGE.falsestep, { color: MIRROR, accent: MIRROR_CORE, release: 'swing', swing: 'slash', impact: 'slash', scale: 0.9 }),
  [MIRAGE.returnBlow]: base(MIRAGE.returnBlow, { color: MIRROR, accent: MIRROR_CORE, release: 'swing', swing: 'spin', impact: 'slash', heal: 'heal', scale: 1.1, big: true }),
  [MIRAGE.phantomLure]: base(MIRAGE.phantomLure, { color: MIRROR, accent: MIRROR_CORE, release: 'beam', impact: 'taunt', scale: 0.9 }),
  [MIRAGE.sidestep]: base(MIRAGE.sidestep, { color: MIRROR, accent: MIRROR_CORE, release: 'flash', scale: 0.8 }),
  [MIRAGE.mirrorSpin]: base(MIRAGE.mirrorSpin, { color: MIRROR, accent: MIRROR_CORE, release: 'swing', swing: 'spin', impact: 'slash', scale: 1.0 }),
  [MIRAGE.haze]: base(MIRAGE.haze, { color: MIRROR, accent: MIRROR_CORE, release: 'nova', zone: 'consecrate', scale: 1.0 }),
  [MIRAGE.mirrorJab]: base(MIRAGE.mirrorJab, { color: MIRROR, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.8 }),
  [MIRAGE.hallOfMirrors]: base(MIRAGE.hallOfMirrors, { color: MIRROR, accent: MIRROR_CORE, release: 'nova', impact: 'taunt', scale: 1.3, big: true }),
  [MIRAGE_AURA.phantoms]: base(MIRAGE_AURA.phantoms, { color: MIRROR, accent: MIRROR_CORE, heal: 'heal', scale: 0.7 }),
  [VENOMWRIGHT.venomEdge]: base(VENOMWRIGHT.venomEdge, { color: TOXIN, accent: TOXIN_CORE, release: 'swing', swing: 'slash', impact: 'venom', scale: 0.85 }),
  [VENOMWRIGHT.catalyst]: base(VENOMWRIGHT.catalyst, { color: TOXIN, accent: TOXIN_CORE, release: 'swing', swing: 'thrust', impact: 'venom', scale: 1.2, big: true }),
  [VENOMWRIGHT.creepingFume]: base(VENOMWRIGHT.creepingFume, { color: TOXIN, accent: TOXIN_CORE, cast: 'venom', release: 'flash', scale: 0.8 }),
  [VENOMWRIGHT.paralyticOpener]: base(VENOMWRIGHT.paralyticOpener, { color: TOXIN, accent: TOXIN_CORE, release: 'swing', swing: 'thrust', impact: 'venom', scale: 1.0, big: true }),
  [VENOMWRIGHT.causticRoll]: base(VENOMWRIGHT.causticRoll, { color: TOXIN, accent: TOXIN_CORE, release: 'flash', impact: 'venom', zone: 'venomPool', tick: 'venom', scale: 0.9 }),
  [VENOMWRIGHT.flaskToss]: base(VENOMWRIGHT.flaskToss, { color: TOXIN, accent: TOXIN_CORE, release: 'projectile', projectile: 'venom', speed: 26, impact: 'venom', scale: 1.0 }),
  [VENOMWRIGHT.chokingGas]: base(VENOMWRIGHT.chokingGas, { color: TOXIN, accent: TOXIN_CORE, release: 'nova', zone: 'venomPool', scale: 1.0 }),
  [VENOMWRIGHT.numbingJab]: base(VENOMWRIGHT.numbingJab, { color: TOXIN, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'venom', scale: 0.8 }),
  [VENOMWRIGHT.plaguebloom]: base(VENOMWRIGHT.plaguebloom, { color: TOXIN, accent: TOXIN_CORE, release: 'projectile', projectile: 'venom', speed: 24, impact: 'venom', zone: 'venomPool', tick: 'venom', scale: 1.1 }),
  [VENOMWRIGHT_AURA.corrode]: base(VENOMWRIGHT_AURA.corrode, { color: TOXIN, accent: TOXIN_CORE, tick: 'venom' }),
  [VENOMWRIGHT_AURA.numb]: base(VENOMWRIGHT_AURA.numb, { color: [0.4, 0.7, 0.55], accent: [0.8, 1.0, 0.85], tick: 'venom' }),
  [VENOMWRIGHT_AURA.blight]: base(VENOMWRIGHT_AURA.blight, { color: [0.62, 0.78, 0.15], accent: TOXIN_CORE, tick: 'venom' }),
  [VENOMWRIGHT_AURA.catalysis]: base(VENOMWRIGHT_AURA.catalysis, { color: TOXIN, accent: TOXIN_CORE, impact: 'venom', scale: 1.25, big: true }),
  // +acolyte-specs: Lightbinder (ward-gold light; Absolution heals rise as motes on the marked).
  [LIGHTBINDER.dawnlash]: base(LIGHTBINDER.dawnlash, { color: WARD, accent: WARD_CORE, cast: 'holy', release: 'projectile', projectile: 'holy', speed: 40, impact: 'holy', scale: 1.0 }),
  [LIGHTBINDER.triuneRay]: base(LIGHTBINDER.triuneRay, { color: WARD, accent: WARD_CORE, cast: 'holy', release: 'beam', impact: 'holyBurst', scale: 0.95 }),
  [LIGHTBINDER.quickmend]: base(LIGHTBINDER.quickmend, { color: WARD, accent: WARD_CORE, cast: 'holy', release: 'flash', heal: 'bigHeal', scale: 0.95 }),
  [LIGHTBINDER.radiantWard]: base(LIGHTBINDER.radiantWard, { color: WARD, accent: WARD_CORE, release: 'flash', heal: 'heal', scale: 0.9 }),
  [LIGHTBINDER.haloburst]: base(LIGHTBINDER.haloburst, { color: WARD, accent: WARD_CORE, release: 'nova', heal: 'heal', scale: 1.3, big: true }),
  [LIGHTBINDER.lightboundOath]: base(LIGHTBINDER.lightboundOath, { color: WARD, accent: WARD_CORE, release: 'nova', heal: 'motes', scale: 1.0 }),
  [LIGHTBINDER.blindingVerdict]: base(LIGHTBINDER.blindingVerdict, { color: WARD_CORE, accent: WARD_CORE, release: 'flash', impact: 'holyBurst', heal: 'heal', scale: 1.1, big: true }),
  [LIGHTBINDER.exaltation]: base(LIGHTBINDER.exaltation, { color: WARD, accent: WARD_CORE, release: 'nova', scale: 1.35, big: true }),
  [LIGHTBINDER_AURA.absolution]: base(LIGHTBINDER_AURA.absolution, { color: WARD, accent: WARD_CORE, heal: 'motes', scale: 0.8 }),
  // Hemomancer: blood bolts, a pool of blood, a field that bleeds the pack.
  [HEMOMANCER.bloodBolt]: base(HEMOMANCER.bloodBolt, { color: VEIN, accent: VEIN_CORE, cast: 'shadow', release: 'projectile', projectile: 'shadow', speed: 34, impact: 'shadow', heal: 'motes', scale: 0.95 }),
  [HEMOMANCER.crimsonBloom]: base(HEMOMANCER.crimsonBloom, { color: VEIN, accent: VEIN_CORE, release: 'flash', zone: 'consecrate', heal: 'heal', scale: 1.0 }),
  [HEMOMANCER.transfusion]: base(HEMOMANCER.transfusion, { color: VEIN, accent: VEIN_CORE, cast: 'shadow', release: 'beam', heal: 'bigHeal', scale: 1.0 }),
  [HEMOMANCER.scab]: base(HEMOMANCER.scab, { color: VEIN, accent: VEIN_CORE, release: 'flash', scale: 0.9 }),
  [HEMOMANCER.bleedfield]: base(HEMOMANCER.bleedfield, { color: VEIN, accent: VEIN_CORE, release: 'flash', zone: 'venomPool', tick: 'bleed', impact: 'shadow', heal: 'heal', decal: 'glyph', scale: 1.1 }),
  [HEMOMANCER.veinPact]: base(HEMOMANCER.veinPact, { color: VEIN, accent: VEIN_CORE, release: 'nova', heal: 'motes', scale: 1.0 }),
  [HEMOMANCER.coagulate]: base(HEMOMANCER.coagulate, { color: VEIN, accent: VEIN_CORE, release: 'flash', impact: 'shadow', scale: 0.9 }),
  [HEMOMANCER.martyr]: base(HEMOMANCER.martyr, { color: VEIN, accent: VEIN_CORE, release: 'nova', scale: 1.25, big: true }),
  [HEMOMANCER_AURA.martyr]: base(HEMOMANCER_AURA.martyr, { color: VEIN, accent: VEIN_CORE, tick: 'bleed', heal: 'bigHeal', scale: 1.0 }),
  // Heretic: hex-fire, a rotting curse, a blighted field, the Hollow One's claws.
  [HERETIC.hexbolt]: base(HERETIC.hexbolt, { color: HOLLOW, accent: HOLLOW_CORE, cast: 'shadow', release: 'projectile', projectile: 'shadow', speed: 36, impact: 'shadow', scale: 1.0 }),
  [HERETIC.rotcurse]: base(HERETIC.rotcurse, { color: HOLLOW, accent: HOLLOW_CORE, cast: 'shadow', release: 'flash', impact: 'shadow', scale: 0.85 }),
  [HERETIC.unholyTithe]: base(HERETIC.unholyTithe, { color: HOLLOW, accent: VEIN_CORE, release: 'flash', impact: 'shadow', scale: 0.8 }),
  [HERETIC.wraithWard]: base(HERETIC.wraithWard, { color: HOLLOW, accent: HOLLOW_CORE, release: 'flash', scale: 0.9 }),
  [HERETIC.blightField]: base(HERETIC.blightField, { color: HOLLOW, accent: HOLLOW_CORE, release: 'flash', zone: 'venomPool', tick: 'school', impact: 'shadow', decal: 'glyph', scale: 1.1 }),
  [HERETIC.hollowCovenant]: base(HERETIC.hollowCovenant, { color: HOLLOW, accent: HOLLOW_CORE, release: 'nova', heal: 'motes', scale: 1.0 }),
  [HERETIC.dreadGaze]: base(HERETIC.dreadGaze, { color: HOLLOW, accent: HOLLOW_CORE, release: 'beam', impact: 'shadow', scale: 0.9 }),
  [HERETIC.soulflay]: base(HERETIC.soulflay, { color: HOLLOW, accent: HOLLOW_CORE, cast: 'shadow', release: 'beam', impact: 'shadow', decal: 'glyph', scale: 1.3, big: true }),
  [HERETIC.callHollowOne]: base(HERETIC.callHollowOne, { color: HOLLOW, accent: HOLLOW_CORE, release: 'nova', impact: 'shadow', decal: 'glyph', scale: 1.4, big: true }),
  [HERETIC.hollowRend]: base(HERETIC.hollowRend, { color: HOLLOW, accent: HOLLOW_CORE, impact: 'slash', scale: 1.0 }), // the pet's claw: impact only (it is logged as the Heretic's)
  [HERETIC_AURA.rotcurse]: base(HERETIC_AURA.rotcurse, { color: HOLLOW, accent: HOLLOW_CORE, tick: 'school', scale: 0.8 }),
  // +ranger-specs: Deadeye (brass tracers and muzzle flashes; the perfect reload's gold glint).
  [DEADEYE.quickdraw]: base(DEADEYE.quickdraw, { color: BRASS, accent: BRASS_CORE, release: 'projectile', projectile: 'bullet', speed: 140, impact: 'sparks', scale: 0.9 }),
  [DEADEYE.fanTheHammer]: base(DEADEYE.fanTheHammer, { color: BRASS, accent: BRASS_CORE, release: 'projectile', projectile: 'bullet', speed: 150, impact: 'sparks', scale: 1.35, big: true }),
  [DEADEYE.flashbang]: base(DEADEYE.flashbang, { color: [1.0, 0.95, 0.82], accent: [1, 1, 1], release: 'projectile', projectile: 'orb', speed: 30, impact: 'holyBurst', scale: 1.25, big: true }),
  [DEADEYE.rollReload]: base(DEADEYE.rollReload, { color: [0.75, 0.66, 0.52], accent: BRASS_CORE, release: 'flash', scale: 0.7 }),
  [DEADEYE.snapShut]: base(DEADEYE.snapShut, { color: BRASS, accent: BRASS_CORE, release: 'flash', impact: 'none', scale: 1.0 }),
  [DEADEYE.ricochet]: base(DEADEYE.ricochet, { color: BRASS, accent: BRASS_CORE, release: 'projectile', projectile: 'bullet', speed: 130, impact: 'sparks', scale: 1.05 }),
  [DEADEYE.trailStride]: base(DEADEYE.trailStride, { color: [0.85, 0.75, 0.55], accent: BRASS_CORE, release: 'flash', scale: 0.7 }),
  [DEADEYE.disarmingShot]: base(DEADEYE.disarmingShot, { color: BRASS, accent: C.interruptCore, release: 'projectile', projectile: 'bullet', speed: 150, impact: 'sparks', scale: 0.9 }),
  [DEADEYE.highNoon]: base(DEADEYE.highNoon, { color: BRASS, accent: BRASS_CORE, release: 'nova', impact: 'none', scale: 1.2, big: true }),
  [DEADEYE_AURA.highNoonMark]: base(DEADEYE_AURA.highNoonMark, { color: BRASS, accent: BRASS_CORE, impact: 'execute', scale: 1.15, big: true }), // the volley lands when the mark ends
  // Grovewarden: a vine whip (it reaches allies and enemies alike), blossoms on every heal, bloom bursts.
  [GROVEWARDEN.budding]: base(GROVEWARDEN.budding, { color: LEAF, accent: LEAF_CORE, release: 'beam', impact: 'venom', heal: 'motes', scale: 0.9 }),
  [GROVEWARDEN.burstbloom]: base(GROVEWARDEN.burstbloom, { color: BLOSSOM, accent: BLOSSOM_CORE, release: 'flash', heal: 'bigHeal', scale: 1.15, big: true }),
  [GROVEWARDEN.snarlroot]: base(GROVEWARDEN.snarlroot, { color: LEAF, accent: LEAF_CORE, release: 'nova', impact: 'venom', decal: 'cracks', scale: 1.1 }),
  [GROVEWARDEN.wildstride]: base(GROVEWARDEN.wildstride, { color: BLOSSOM, accent: BLOSSOM_CORE, release: 'flash', zone: 'consecrate', heal: 'motes', scale: 0.9 }),
  [GROVEWARDEN.overgrowthSurge]: base(GROVEWARDEN.overgrowthSurge, { color: LEAF, accent: LEAF_CORE, release: 'nova', heal: 'heal', scale: 1.3, big: true }),
  [GROVEWARDEN.groveStride]: base(GROVEWARDEN.groveStride, { color: LEAF, accent: LEAF_CORE, release: 'nova', heal: 'motes', scale: 1.0 }),
  [GROVEWARDEN.thornpin]: base(GROVEWARDEN.thornpin, { color: LEAF, accent: C.interruptCore, release: 'projectile', projectile: 'venom', speed: 60, impact: 'sparks', scale: 0.85 }),
  [GROVEWARDEN.lifevine]: base(GROVEWARDEN.lifevine, { color: LEAF, accent: LEAF_CORE, release: 'beam', heal: 'heal', scale: 1.0 }),
  [GROVEWARDEN.worldroot]: base(GROVEWARDEN.worldroot, { color: LEAF, accent: LEAF_CORE, release: 'nova', decal: 'cracks', scale: 1.4, big: true }),
  [GROVEWARDEN_AURA.budding]: base(GROVEWARDEN_AURA.budding, { color: LEAF, accent: BLOSSOM_CORE, heal: 'motes', scale: 0.75 }),
  [GROVEWARDEN_AURA.lifevine]: base(GROVEWARDEN_AURA.lifevine, { color: BLOSSOM, accent: BLOSSOM_CORE, heal: 'heal', scale: 0.9 }),
  [GROVEWARDEN_AURA.petalfall]: base(GROVEWARDEN_AURA.petalfall, { color: BLOSSOM, accent: BLOSSOM_CORE, heal: 'motes', scale: 0.7 }),
  [GROVEWARDEN_AURA.quickening]: base(GROVEWARDEN_AURA.quickening, { color: LEAF, accent: LEAF_CORE, heal: 'motes', scale: 0.7 }),
  // Beastbound: amber arrows, the spirit beast's bites, a stampeding herd.
  [BEASTBOUND.markPrey]: base(BEASTBOUND.markPrey, { color: SPIRIT, accent: SPIRIT_CORE, release: 'projectile', projectile: 'arrow', speed: 60, impact: 'sparks', scale: 1.0 }),
  [BEASTBOUND.packFury]: base(BEASTBOUND.packFury, { color: SPIRIT, accent: SPIRIT_CORE, release: 'projectile', projectile: 'arrow', speed: 75, impact: 'slash', scale: 1.3, big: true }),
  [BEASTBOUND.spiritSnare]: base(BEASTBOUND.spiritSnare, { color: SPIRIT, accent: SPIRIT_CORE, release: 'flash', scale: 0.8 }),
  [BEASTBOUND.boundLeap]: base(BEASTBOUND.boundLeap, { color: SPIRIT, accent: SPIRIT_CORE, release: 'flash', scale: 0.8 }),
  [BEASTBOUND.spiritHerd]: base(BEASTBOUND.spiritHerd, { color: SPIRIT, accent: SPIRIT_CORE, release: 'charge', impact: 'heavy', decal: 'cracks', scale: 1.25, big: true }),
  [BEASTBOUND.packStride]: base(BEASTBOUND.packStride, { color: SPIRIT, accent: SPIRIT_CORE, release: 'flash', scale: 0.7 }),
  [BEASTBOUND.howlShot]: base(BEASTBOUND.howlShot, { color: SPIRIT, accent: C.interruptCore, release: 'projectile', projectile: 'arrow', speed: 75, impact: 'sparks', scale: 0.9 }),
  [BEASTBOUND.spiritMerge]: base(BEASTBOUND.spiritMerge, { color: SPIRIT, accent: SPIRIT_CORE, release: 'nova', scale: 1.3, big: true }),
  [BEASTBOUND.guard]: base(BEASTBOUND.guard, { color: [0.62, 0.82, 1.0], accent: [0.9, 0.96, 1.0], release: 'flash', scale: 0.7 }),
  [BEASTBOUND.hunt]: base(BEASTBOUND.hunt, { color: SPIRIT, accent: SPIRIT_CORE, release: 'flash', scale: 0.7 }),
  [BEASTBOUND.maul]: base(BEASTBOUND.maul, { color: SPIRIT, accent: SPIRIT_CORE, release: 'none', scale: 0.8 }),
  [BEASTBOUND.pounce]: base(BEASTBOUND.pounce, { color: SPIRIT, accent: SPIRIT_CORE, release: 'swing', swing: 'slash', impact: 'slash', scale: 0.85 }),
  [BEASTBOUND.furyBite]: base(BEASTBOUND.furyBite, { color: SPIRIT, accent: SPIRIT_CORE, release: 'swing', swing: 'spin', impact: 'slash', scale: 1.0, big: true }),
  [BEASTBOUND.spiritPin]: base(BEASTBOUND.spiritPin, { color: SPIRIT, accent: SPIRIT_CORE, release: 'swing', swing: 'overhead', impact: 'heavy', scale: 1.0 }),
  [BEASTBOUND.interpose]: base(BEASTBOUND.interpose, { color: [0.62, 0.82, 1.0], accent: SPIRIT_CORE, release: 'nova', impact: 'taunt', scale: 1.0 }),
  [BEASTBOUND.maulBite]: base(BEASTBOUND.maulBite, { color: SPIRIT, accent: SPIRIT_CORE, release: 'swing', swing: 'overhead', impact: 'execute', tick: 'bleed', scale: 1.15, big: true }),
  [BEASTBOUND.growl]: base(BEASTBOUND.growl, { color: [0.62, 0.82, 1.0], accent: SPIRIT_CORE, release: 'flash', impact: 'taunt', scale: 0.8 }),
  [BEASTBOUND.howl]: base(BEASTBOUND.howl, { color: SPIRIT, accent: SPIRIT_CORE, release: 'nova', scale: 0.8 }),
  [BEASTBOUND.spiritRend]: base(BEASTBOUND.spiritRend, { color: SPIRIT, accent: SPIRIT_CORE, release: 'swing', swing: 'wide', impact: 'slash', scale: 1.1 }),
  [BEASTBOUND_AURA.maulBleed]: base(BEASTBOUND_AURA.maulBleed, { color: SPIRIT, accent: SPIRIT_CORE, tick: 'bleed', scale: 0.7 }),
};

/**
 * +ranger-specs: Auto Shot per spec (the shot is one ability for everyone): the Deadeye's pistol fires
 * a bullet with a muzzle flash, not an arrow.
 */
export const SPEC_AUTO_VFX: Readonly<Record<string, VfxSpec>> = {
  deadeye: base('deadeye.auto_shot', { color: BRASS, accent: BRASS_CORE, release: 'projectile', projectile: 'bullet', speed: 130, impact: 'sparks', scale: 0.8 }),
};

export const ABILITY_VFX: Readonly<Record<AbilityId, VfxSpec>> = {
  ...SPEC_VFX,
  // Stormcaller
  [S.lightningBolt]: base(S.lightningBolt, { color: C.storm, accent: C.stormCore, cast: 'storm', release: 'bolt', impact: 'lightning' }),
  [S.lavaBurst]: base(S.lavaBurst, { color: C.lava, accent: C.lavaCore, cast: 'flame', release: 'projectile', projectile: 'lava', speed: 34, impact: 'fire', decal: 'scorch', scale: 1.15, big: true }),
  [S.chainLightning]: base(S.chainLightning, { color: C.storm, accent: C.stormCore, cast: 'storm', release: 'chain', impact: 'lightning', scale: 0.9 }),
  [S.earthquake]: base(S.earthquake, { color: C.earth, accent: C.quakeGlow, release: 'flash', zone: 'quake', tick: 'quake' }),
  [S.stormkeeper]: base(S.stormkeeper, { color: C.storm, accent: C.stormCore, release: 'nova', scale: 0.7 }),
  [S.flameShock]: base(S.flameShock, { color: C.flame, accent: C.lavaCore, release: 'flash', impact: 'flameLick', tick: 'flameLick', scale: 0.85 }),
  [S.windShear]: base(S.windShear, { color: C.wind, accent: C.interruptCore, release: 'projectile', projectile: 'wind', speed: 60, impact: 'none' }),
  [S.astralShift]: base(S.astralShift, { color: [0.55, 0.7, 1.0], accent: [0.9, 0.95, 1.0], release: 'flash' }),
  // Rotation rework: an amber earth bolt that cracks the floor under the target, a sustained arc
  // torrent (a bolt on release, lightning on every tick), and Tempest's heavy forked storm.
  [S.earthShock]: base(S.earthShock, { color: C.amber, accent: C.amberCore, release: 'bolt', impact: 'smash', decal: 'cracks', scale: 0.95, big: true }),
  [S.arcTorrent]: base(S.arcTorrent, { color: C.arc, accent: C.stormCore, cast: 'storm', release: 'bolt', impact: 'lightning', scale: 0.85 }),
  [S.tempest]: base(S.tempest, { color: C.tempest, accent: C.stormCore, cast: 'storm', release: 'chain', impact: 'lightning', scale: 1.3, big: true }),
  // Warden
  [W.shieldSlam]: base(W.shieldSlam, { color: C.shield, accent: C.shieldCore, release: 'swing', swing: 'thrust', impact: 'shieldSlam', big: true }),
  [W.cleaveStrike]: base(W.cleaveStrike, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'wide', impact: 'slash', scale: 0.85 }),
  [W.bulwarkStrike]: base(W.bulwarkStrike, { color: C.shield, accent: C.shieldCore, release: 'swing', swing: 'overhead', impact: 'heavy', heal: 'heal' }),
  [W.consecratedGround]: base(W.consecratedGround, { color: C.holy, accent: C.holyCore, release: 'nova', zone: 'consecrate', tick: 'holy', scale: 1.2 }),
  [W.lastStand]: base(W.lastStand, { color: C.shield, accent: C.shieldCore, release: 'flash' }),
  [W.shieldWallBash]: base(W.shieldWallBash, { color: C.shield, accent: C.shieldCore, release: 'flash', scale: 0.8 }),
  [W.shieldBash]: base(W.shieldBash, { color: C.shield, accent: C.shieldCore, release: 'swing', swing: 'thrust', impact: 'sparks' }),
  [W.taunt]: base(W.taunt, { color: C.rage, accent: C.rageCore, release: 'flash', impact: 'taunt' }),
  [W.grip]: base(W.grip, { color: C.shadow, accent: [0.8, 0.7, 1.0], release: 'beam', impact: 'shadow' }),
  // Rotation rework: the thrown aegis is a golden holy disc that bursts on each bounce; the recall
  // is a gold beam back along the line; Riposte is a silver-blue fan cut.
  [W.aegisThrow]: base(W.aegisThrow, { color: C.aegis, accent: C.holyCore, release: 'projectile', projectile: 'holy', speed: 38, impact: 'shieldSlam', scale: 0.9 }),
  [W.aegisRecall]: base(W.aegisRecall, { color: C.aegis, accent: C.holyCore, release: 'beam', impact: 'sparks', scale: 0.85 }),
  [W.riposte]: base(W.riposte, { color: C.riposte, accent: C.steelCore, release: 'swing', swing: 'wide', impact: 'slash', scale: 0.95 }),
  // Lightbinder
  [L.flashHeal]: base(L.flashHeal, { color: C.heal, accent: C.healCore, cast: 'holy', release: 'flash', heal: 'heal' }),
  [L.holyShock]: base(L.holyShock, { color: C.holy, accent: C.holyCore, release: 'flash', impact: 'holyBurst', heal: 'holyBurst' }),
  [L.wordOfLight]: base(L.wordOfLight, { color: C.heal, accent: C.holyCore, release: 'flash', heal: 'bigHeal', scale: 1.2 }),
  [L.judgment]: base(L.judgment, { color: C.holy, accent: C.holyCore, release: 'projectile', projectile: 'holy', speed: 48, impact: 'holy' }),
  [L.radiantWave]: base(L.radiantWave, { color: C.heal, accent: C.holyCore, release: 'wave', heal: 'motes', tick: 'motes' }),
  [L.guardiansAegis]: base(L.guardiansAegis, { color: [0.95, 0.85, 0.5], accent: C.holyCore, release: 'flash' }),
  [L.cleanse]: base(L.cleanse, { color: C.heal, accent: C.healCore, release: 'flash', impact: 'cleanse' }),
  [L.divineShield]: base(L.divineShield, { color: C.holy, accent: C.holyCore, release: 'flash' }),
  [L.blessingOfFreedom]: base(L.blessingOfFreedom, { color: [0.6, 0.85, 1.0], accent: C.healCore, release: 'flash' }),
  // Rotation rework, dawn palette: Dawnsong rings out a nova and lands big pillars; Dawnbringer
  // sweeps a wave of dawn light; Sunfall drops a hot sun orb that bursts on its target.
  [L.dawnsong]: base(L.dawnsong, { color: C.dawn, accent: C.holyCore, release: 'nova', heal: 'bigHeal', scale: 0.9 }),
  [L.dawnbringer]: base(L.dawnbringer, { color: C.dawn, accent: C.holyCore, release: 'wave', impact: 'holyBurst', heal: 'holyBurst', scale: 1.2, big: true }),
  [L.sunfall]: base(L.sunfall, { color: C.sun, accent: C.sunCore, release: 'projectile', projectile: 'holy', speed: 30, impact: 'holyBurst', heal: 'bigHeal', scale: 1.3, big: true }),
  // Blademaster
  [B.mortalStrike]: base(B.mortalStrike, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'overhead', impact: 'heavy', big: true, scale: 1.1 }),
  [B.overpower]: base(B.overpower, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'slash', impact: 'slash' }),
  [B.cleave]: base(B.cleave, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'wide', impact: 'slash', scale: 0.85 }),
  [B.execute]: base(B.execute, { color: C.rage, accent: C.rageCore, release: 'swing', swing: 'overhead', impact: 'execute', big: true, scale: 1.25 }),
  [B.bladestorm]: base(B.bladestorm, { color: C.steel, accent: C.steelCore, impact: 'slash', tick: 'blade', scale: 0.6 }),
  [B.colossusSmash]: base(B.colossusSmash, { color: C.rage, accent: C.rageCore, release: 'swing', swing: 'overhead', impact: 'smash', big: true }),
  [B.pummel]: base(B.pummel, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.8 }),
  [B.dieByTheSword]: base(B.dieByTheSword, { color: C.steel, accent: C.steelCore, release: 'flash' }),
  // +classes: the Ranger (no old kit to borrow): wood-and-steel arrows, a rain of shafts, a snare.
  [RA.trueDraw]: base(RA.trueDraw, { color: C.arrow, accent: C.arrowCore, release: 'projectile', projectile: 'arrow', speed: 55, impact: 'sparks', scale: 1.0 }),
  [RA.longshot]: base(RA.longshot, { color: C.arrow, accent: C.arrowCore, release: 'projectile', projectile: 'arrow', speed: 70, impact: 'heavy', scale: 1.4, big: true }),
  [RA.arrowfall]: base(RA.arrowfall, { color: C.arrow, accent: C.arrowCore, release: 'flash', zone: 'quake', tick: 'blade', scale: 0.9 }),
  [RA.snareCoil]: base(RA.snareCoil, { color: [0.5, 0.75, 0.4], accent: [0.85, 1.0, 0.7], release: 'flash', zone: 'snare', impact: 'sparks', scale: 0.8 }),
  [RA.swiftfoot]: base(RA.swiftfoot, { color: [0.6, 0.9, 0.5], accent: [0.9, 1.0, 0.8], release: 'flash', scale: 0.6 }),
  // +autoattack: Auto Shot: a plain, smaller arrow (the white shot between the Ranger's buttons).
  [AUTO_SHOT_ABILITY_ID]: base(AUTO_SHOT_ABILITY_ID, { color: C.arrow, accent: C.arrowCore, release: 'projectile', projectile: 'arrow', speed: 60, impact: 'sparks', scale: 0.85 }),
  [RA.pinShot]: base(RA.pinShot, { color: C.arrow, accent: C.interruptCore, release: 'projectile', projectile: 'arrow', speed: 75, impact: 'sparks', scale: 0.9 }),
  // +basekits: the new base-kit abilities (others borrow an old look through ui.animAs).
  [WA.headlong]: base(WA.headlong, { color: C.steel, accent: C.steelCore, release: 'charge', impact: 'heavy', scale: 0.85 }),
  [WA.rive]: base(WA.rive, { color: C.blood, accent: C.rageCore, release: 'swing', swing: 'slash', impact: 'slash', tick: 'bleed', scale: 0.9 }),
  [WA.bloodprize]: base(WA.bloodprize, { color: C.blood, accent: C.rageCore, release: 'swing', swing: 'overhead', impact: 'heavy', heal: 'heal', scale: 1.05, big: true }),
  [RO.lurk]: base(RO.lurk, { color: [0.45, 0.4, 0.6], accent: [0.75, 0.7, 0.9], release: 'flash', scale: 0.55 }),
  [RO.dirtyOpener]: base(RO.dirtyOpener, { color: [0.55, 0.45, 0.8], accent: C.steelCore, release: 'swing', swing: 'thrust', impact: 'heavy', scale: 0.95 }),
  [RO.throatjab]: base(RO.throatjab, { color: C.steel, accent: C.interruptCore, release: 'swing', swing: 'thrust', impact: 'sparks', scale: 0.8 }),
  [MA.hoarbind]: base(MA.hoarbind, { color: FROST, accent: FROST_CORE, release: 'nova', impact: 'frost', decal: 'frost', scale: 1.3 }),
  [MA.wanderingStar]: base(MA.wanderingStar, { color: STAR, accent: STAR_CORE, release: 'flash', zone: 'star', impact: 'lightning', scale: 1.2, big: true }),
  [AC.dawnbrand]: base(AC.dawnbrand, { color: C.dawn, accent: C.holyCore, release: 'flash', impact: 'holy', tick: 'holy', scale: 0.9 }),
  [AC.hearthward]: base(AC.hearthward, { color: [1.0, 0.75, 0.4], accent: C.holyCore, release: 'nova', heal: 'motes', scale: 1.1 }),
  [AC.dazzle]: base(AC.dazzle, { color: C.sun, accent: C.sunCore, release: 'flash', impact: 'holyBurst', scale: 1.0 }),
  [RA.backflip]: base(RA.backflip, { color: C.steel, accent: C.steelCore, release: 'charge', scale: 0.5 }),
  // Dodge (Shift, every hero): a light dash puff, no impact.
  [ABILITY.common.dodge]: base(ABILITY.common.dodge, { color: C.steel, accent: C.steelCore, release: 'charge', scale: 0.5 }),
  [B.charge]: base(B.charge, { color: C.steel, accent: C.steelCore, release: 'charge', impact: 'heavy', scale: 0.8 }),
  // Rotation rework: Slam a quick heavy chop, Rend a crimson cut that bleeds, Ravager's Finale the
  // biggest blow in the kit (cracks and debris on every Wounded target).
  [B.slam]: base(B.slam, { color: C.rage, accent: C.rageCore, release: 'swing', swing: 'overhead', impact: 'heavy', scale: 0.85 }),
  [B.rend]: base(B.rend, { color: C.blood, accent: C.rageCore, release: 'swing', swing: 'slash', impact: 'slash', tick: 'bleed', scale: 0.9 }),
  [B.ravagersFinale]: base(B.ravagersFinale, { color: C.blood, accent: C.rageCore, release: 'swing', swing: 'spin', impact: 'execute', big: true, scale: 1.3 }),
};

/** Persistent aura loops (applied → loop until removed). */
export const AURA_VFX: Readonly<Record<string, AuraVfx>> = {
  [AURA.stormkeeper]: { style: 'stormkeeper', color: C.storm, accent: C.stormCore },
  [AURA.lastStand]: { style: 'bubble', color: C.shield, accent: C.shieldCore },
  [AURA.shieldWallBash]: { style: 'shieldWall', color: C.shield, accent: C.shieldCore },
  [AURA.divineShield]: { style: 'divine', color: C.holy, accent: C.holyCore },
  [AURA.guardiansAegis]: { style: 'aegis', color: [0.95, 0.85, 0.5], accent: C.holyCore },
  [AURA.astralShift]: { style: 'astral', color: [0.55, 0.7, 1.0], accent: [0.9, 0.95, 1.0] },
  [AURA.bladestorm]: { style: 'bladestorm', color: C.steel, accent: C.steelCore },
  [AURA.dieByTheSword]: { style: 'parry', color: C.steel, accent: C.steelCore },
  [AURA.flameShock]: { style: 'burning', color: C.flame, accent: C.lavaCore },
  [AURA.colossusSmash]: { style: 'smash', color: C.rage, accent: C.rageCore },
  [AURA.radiantWaveHot]: { style: 'motes', color: C.heal, accent: C.healCore },
  [ABILITY.lightbinder.blessingOfFreedom]: { style: 'freedom', color: [0.6, 0.85, 1.0], accent: C.healCore },
  // +basekits
  [BASE_AURA.rive]: { style: 'bleed', color: C.blood, accent: C.rageCore },
  [BASE_AURA.dawnbrand]: { style: 'burning', color: C.dawn, accent: C.holyCore },
  [BASE_AURA.hoarbound]: { style: 'petrified', color: FROST, accent: FROST_CORE },
  [BASE_AURA.snareCoil]: { style: 'freedom', color: [0.5, 0.75, 0.4], accent: [0.85, 1.0, 0.7] },
  [BASE_AURA.lurk]: { style: 'astral', color: [0.45, 0.4, 0.6], accent: [0.75, 0.7, 0.9] },
  [BASE_AURA.swiftfoot]: { style: 'motes', color: [0.6, 0.9, 0.5], accent: [0.9, 1.0, 0.8] },
  [BASE_AURA.ironBreath]: { style: 'bubble', color: C.shield, accent: C.shieldCore },
  [BASE_AURA.ashveil]: { style: 'parry', color: [0.5, 0.5, 0.55], accent: [0.8, 0.8, 0.85] },
  // +mage-specs
  [STORMCALLER_AURA.overvolt]: { style: 'stormkeeper', color: VOLT, accent: VOLT_CORE },
  [STORMCALLER_AURA.stormheart]: { style: 'stormkeeper', color: VOLT, accent: VOLT_CORE },
  [STORMCALLER_AURA.arcWard]: { style: 'bubble', color: VOLT, accent: VOLT_CORE },
  [RIMECALLER_AURA.frozen]: { style: 'petrified', color: RIME, accent: RIME_CORE },
  [RIMECALLER_AURA.frostMantle]: { style: 'bubble', color: RIME, accent: RIME_CORE },
  [RIMECALLER_AURA.deepWinter]: { style: 'astral', color: RIME, accent: RIME_CORE },
  [CINDERBORN_AURA.phoenix]: { style: 'burning', color: C.sun, accent: C.sunCore },
  [CINDERBORN_AURA.smoulder]: { style: 'burning', color: EMBER, accent: EMBER_CORE },
  [CINDERBORN_AURA.kindledHeart]: { style: 'burning', color: EMBER, accent: EMBER_CORE },
  [CINDERBORN_AURA.ashrisePact]: { style: 'bubble', color: EMBER, accent: EMBER_CORE },
  [ENEMY_AURA.hypnosis]: { style: 'hypnosis', color: C.hostileShadow, accent: [1.0, 0.6, 0.9] },
  [ENEMY_AURA.petrified]: { style: 'petrified', color: [0.6, 0.58, 0.55], accent: [0.9, 0.85, 0.75] },
  [ENEMY_AURA.scaleWard]: { style: 'ward', color: C.venom, accent: C.venomCore },
  [ENEMY_AURA.acidBurn]: { style: 'venom', color: C.venom, accent: C.venomCore },
  [ENEMY_AURA.venomNova]: { style: 'venom', color: C.venom, accent: C.venomCore },
  [ENEMY_AURA.toxicAtrophy]: { style: 'venom', color: C.venom, accent: C.venomCore },
  [ENEMY_AURA.lacerate]: { style: 'bleed', color: C.hostilePhys, accent: C.hostilePhysCore },
  [ENEMY_AURA.boneslice]: { style: 'bleed', color: C.hostilePhys, accent: C.hostilePhysCore },
  [ENEMY_AURA.fangRitual]: { style: 'ritual', color: C.venom, accent: C.venomCore },
  // +rogue-specs
  [SHADOWDANCER_AURA.veilOfNight]: { style: 'astral', color: SHADE, accent: SHADE_CORE },
  [SHADOWDANCER_AURA.duskwalk]: { style: 'astral', color: [0.3, 0.24, 0.5], accent: SHADE_CORE },
  [SHADOWDANCER_AURA.duskwalkFight]: { style: 'astral', color: [0.3, 0.24, 0.5], accent: SHADE_CORE },
  [SHADOWDANCER_AURA.convergence]: { style: 'stormkeeper', color: SHADE, accent: SHADE_CORE },
  [MIRAGE_AURA.hallOfMirrors]: { style: 'bubble', color: MIRROR, accent: MIRROR_CORE },
  [MIRAGE_AURA.sidestepWindow]: { style: 'parry', color: MIRROR, accent: MIRROR_CORE },
  [VENOMWRIGHT_AURA.corrode]: { style: 'venom', color: TOXIN, accent: TOXIN_CORE },
  [VENOMWRIGHT_AURA.gasMask]: { style: 'parry', color: TOXIN, accent: TOXIN_CORE },
  [VENOMWRIGHT_AURA.fume]: { style: 'astral', color: [0.3, 0.5, 0.22], accent: TOXIN_CORE },
  [VENOMWRIGHT_AURA.fumeFight]: { style: 'astral', color: [0.3, 0.5, 0.22], accent: TOXIN_CORE },
  // +acolyte-specs
  [LIGHTBINDER_AURA.radiantWard]: { style: 'aegis', color: WARD, accent: WARD_CORE },
  [LIGHTBINDER_AURA.absolution]: { style: 'motes', color: WARD, accent: WARD_CORE },
  [LIGHTBINDER_AURA.exaltation]: { style: 'divine', color: WARD, accent: WARD_CORE },
  [HEMOMANCER_AURA.scab]: { style: 'bubble', color: VEIN, accent: VEIN_CORE },
  [HEMOMANCER_AURA.martyr]: { style: 'bleed', color: VEIN, accent: VEIN_CORE },
  [HERETIC_AURA.rotcurse]: { style: 'hypnosis', color: HOLLOW, accent: HOLLOW_CORE },
  [HERETIC_AURA.wraithWard]: { style: 'bubble', color: HOLLOW, accent: HOLLOW_CORE },
  // +ranger-specs
  [DEADEYE_AURA.hotRounds]: { style: 'burning', color: BRASS, accent: BRASS_CORE },
  [DEADEYE_AURA.explosiveRound]: { style: 'burning', color: [1.0, 0.5, 0.2], accent: BRASS_CORE },
  [DEADEYE_AURA.jammed]: { style: 'smash', color: [0.5, 0.48, 0.46], accent: [0.85, 0.8, 0.72] },
  [DEADEYE_AURA.rattled]: { style: 'hypnosis', color: [1.0, 0.95, 0.78], accent: [1, 1, 1] },
  [DEADEYE_AURA.highNoonMark]: { style: 'ritual', color: BRASS, accent: BRASS_CORE },
  [DEADEYE_AURA.sprint]: { style: 'motes', color: [0.85, 0.75, 0.55], accent: BRASS_CORE },
  [GROVEWARDEN_AURA.budding]: { style: 'motes', color: LEAF, accent: BLOSSOM_CORE },
  [GROVEWARDEN_AURA.lifevine]: { style: 'freedom', color: LEAF, accent: BLOSSOM_CORE },
  [GROVEWARDEN_AURA.petalfall]: { style: 'motes', color: BLOSSOM, accent: BLOSSOM_CORE },
  [GROVEWARDEN_AURA.snarled]: { style: 'freedom', color: [0.42, 0.62, 0.3], accent: LEAF_CORE },
  [GROVEWARDEN_AURA.worldroot]: { style: 'divine', color: LEAF, accent: LEAF_CORE },
  [BEASTBOUND_AURA.merge]: { style: 'stormkeeper', color: SPIRIT, accent: SPIRIT_CORE },
  [BEASTBOUND_AURA.guardStance]: { style: 'aegis', color: [0.62, 0.82, 1.0], accent: [0.9, 0.96, 1.0] },
  [BEASTBOUND_AURA.marked]: { style: 'smash', color: SPIRIT, accent: SPIRIT_CORE },
  [BEASTBOUND_AURA.pinned]: { style: 'freedom', color: SPIRIT, accent: SPIRIT_CORE },
  [BEASTBOUND_AURA.maulBleed]: { style: 'bleed', color: SPIRIT, accent: SPIRIT_CORE },
};

/** A few enemy abilities that deserve a hand-tuned look beyond the kind fallback. */
const ENEMY_OVERRIDES: Partial<Record<AbilityId, Partial<VfxSpec>>> = {
  [ENEMY.venomBolt]: { projectile: 'venom', speed: 30 },
  [ENEMY.petrifyingGlare]: { cast: 'shadow', color: [0.85, 0.55, 0.4], accent: [1.0, 0.85, 0.6] },
  [ENEMY.boneCyclone]: { cast: 'windup', impact: 'slash' },
  [ENEMY.ravenousStomp]: { impact: 'heavy', release: 'nova' },
  ...ENEMY_VFX, // +encounters: reworked boss/trash looks (fx/enemyTable.ts)
};

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

export const MELEE_KEY = 'melee';

/** What the resolver needs to know about an ability (from the content registry). */
export interface AbilityInfo {
  school: School | null;
  kind?: EnemyAbilityKind;
  targeting?: Targeting;
  melee?: boolean;
  range?: number;
  channel?: boolean;
  castTime?: boolean;
}

export function hasExplicitVfx(abilityId: AbilityId): boolean {
  return Object.prototype.hasOwnProperty.call(ABILITY_VFX, abilityId) || Object.prototype.hasOwnProperty.call(ABILITY_VFX, BASE_VISUAL_ALIAS[abilityId] ?? ''); // +classes
}

function enemySpec(abilityId: string, info: AbilityInfo, hostile: boolean): VfxSpec {
  const school = info.school ?? 'physical';
  const [color, accent] = schoolColors(school, hostile);
  const ranged = (info.range ?? 0) > 8 && info.targeting === 'enemy' && !info.melee;
  const spec = base(`${hostile ? 'hostile' : 'friendly'}:${info.kind ?? 'school'}:${school}`, {
    color,
    accent,
    hostile,
    cast: info.castTime || info.channel ? CAST_BY_SCHOOL[school] : 'none',
    impact: IMPACT_BY_SCHOOL[school],
    heal: 'heal',
    tick: 'school',
  });
  switch (info.kind) {
    case 'bolt':
      spec.release = 'projectile';
      spec.projectile = PROJECTILE_BY_SCHOOL[school];
      spec.speed = 30;
      break;
    case 'tankbuster':
      spec.cast = 'windup';
      spec.release = 'swing';
      spec.swing = 'overhead';
      spec.impact = 'heavy';
      spec.big = true;
      spec.scale = 1.15;
      break;
    case 'frontal':
    case 'groundAoE':
      spec.cast = spec.cast === 'none' ? 'windup' : spec.cast;
      spec.scale = 0.8;
      break;
    case 'pulse':
      spec.release = 'nova';
      spec.scale = 0.9;
      break;
    case 'cc':
      spec.release = ranged ? 'beam' : 'flash';
      break;
    case 'heal':
    case 'shield':
    case 'buff':
      spec.release = 'flash';
      spec.impact = 'none';
      break;
    case 'summon':
      spec.release = 'nova';
      break;
    case 'charge':
      spec.release = 'charge';
      spec.impact = 'heavy';
      break;
    default:
      if (info.melee || (school === 'physical' && !ranged)) {
        spec.release = 'swing';
        spec.swing = 'slash';
        spec.impact = 'sparks';
      } else if (ranged) {
        spec.release = 'projectile';
        spec.projectile = PROJECTILE_BY_SCHOOL[school];
        spec.speed = 34;
      } else spec.release = 'flash';
  }
  const o = ENEMY_OVERRIDES[abilityId];
  if (o) Object.assign(spec, o);
  return spec;
}

const MELEE_FRIENDLY = base(MELEE_KEY, { color: C.steel, accent: C.steelCore, release: 'swing', swing: 'slash', impact: 'sparks', scale: 0.7 });
const MELEE_HOSTILE = base(`${MELEE_KEY}:hostile`, { color: C.hostilePhys, accent: C.hostilePhysCore, release: 'swing', swing: 'slash', impact: 'sparks', scale: 0.7, hostile: true });

const cache = new Map<string, VfxSpec>();

/**
 * Spec for an ability. Explicit table first (player abilities), then enemy kind, then school.
 * `hostile` = cast by an enemy of the party. Results are cached; treat them as read-only.
 */
export function resolveVfx(abilityId: AbilityId, info: AbilityInfo | null | undefined, hostile: boolean, isMelee = false): VfxSpec {
  if (isMelee) return hostile ? MELEE_HOSTILE : MELEE_FRIENDLY;
  const explicit = ABILITY_VFX[abilityId] ?? ABILITY_VFX[BASE_VISUAL_ALIAS[abilityId] ?? '']; // +classes: base kits reuse the old looks
  if (explicit && !hostile) return explicit;
  const key = `${abilityId}|${hostile ? 1 : 0}`;
  const hit = cache.get(key);
  if (hit) return hit;
  let spec: VfxSpec;
  if (explicit) {
    // A player ability used by an enemy (mind control, tests): same shapes, hostile colours.
    const [color, accent] = schoolColors(info?.school ?? null, true);
    spec = { ...explicit, color, accent, hostile: true };
  } else spec = enemySpec(abilityId, info ?? { school: null }, hostile);
  cache.set(key, spec);
  return spec;
}

export function resolveAuraVfx(auraId: string): AuraVfx | null {
  return AURA_VFX[auraId] ?? null;
}
